import React, {useCallback, useEffect, useState} from 'react';
import {AssetManager} from "@dfinity/assets";
import {HttpAgent} from "@dfinity/agent";
import {Principal} from "@dfinity/principal";
import {buildFileTree, filterFileTree} from "./file/fileTreeUtils";
import {FileTreeView} from "./file/FileTreeView";

export interface ProjectFile {
    name: string;    // Includes full path, e.g., "src/main.mo"
    content: string;
    fullId?: string;
    path?: string;
    fileName?: string;
    id?: string;
    // Optional extensions for dual file system support
    fileSystem?: 'backend' | 'frontend' | 'asset';
    assetKey?: string;
    assetUrl?: string;
    displayIcon?: string;
    displayColor?: string;
    tooltipSuffix?: string;
    mimeType?: string;
    size?: number;
}

interface FileTreeNode {
    name: string;
    path: string;
    isDirectory: boolean;
    children?: FileTreeNode[];
    originalFile?: ProjectFile;
    // Fixed file system types
    fileSystemType?: 'backend' | 'frontend' | 'asset';
}

class AssetCanisterService {
    private assetManager: AssetManager | null = null;
    private frontendCanisterId: Principal;
    private identity: any;
    public projectId: string;

    constructor(frontendCanisterId: Principal, identity: any, projectId: string) {
        this.frontendCanisterId = frontendCanisterId;
        this.identity = identity;
        this.projectId = projectId;
    }

    async initialize(): Promise<void> {
        const host = process.env.DFX_NETWORK === "ic" ? "https://icp0.io" : "http://127.0.0.1:4943";

        const agent = new HttpAgent({
            identity: await this.identity,
            host
        });

        if (host.includes('localhost') || host.includes('127.0.0.1')) {
            await agent.fetchRootKey();
        }

        this.assetManager = new AssetManager({
            canisterId: this.frontendCanisterId,
            agent
        });
    }

    public getProjectId(): string {
        return this.projectId;
    }

    async fetchAllAssetFiles(): Promise<ProjectFile[]> {
        if (!this.assetManager) {
            throw new Error('Asset manager not initialized');
        }

        const assetList = await this.assetManager.list();
        const projectPrefix = `projects/${this.projectId}/`;

        const assetFiles: ProjectFile[] = [];
        const processedFolders = new Set<string>();

        // Pre-filter and prepare asset keys in parallel
        const assetKeys: string[] = [];
        for (let i = 0; i < assetList.length; i++) {
            const asset = assetList[i];
            let key = "";
            if (typeof asset === 'string') {
                key = asset;
            } else if (asset && typeof asset === 'object' && 'key' in asset) {
                key = asset.key as string;
            } else {
                continue;
            }

            const cleanKey = key.startsWith('/') ? key.substring(1) : key;
            if (!cleanKey.startsWith(projectPrefix)) {
                continue;
            }

            assetKeys.push(key);
        }

        // Process assets in batches for better performance
        const BATCH_SIZE = 20; // Process 20 assets at a time
        const host = process.env.DFX_NETWORK === "ic" ? "https://icp0.io" : "http://127.0.0.1:4943";

        for (let batchStart = 0; batchStart < assetKeys.length; batchStart += BATCH_SIZE) {
            const batch = assetKeys.slice(batchStart, batchStart + BATCH_SIZE);
            
            // Process batch in parallel
            const batchPromises = batch.map(async (key) => {
                const cleanKey = key.startsWith('/') ? key.substring(1) : key;
                const relativePath = cleanKey.substring(projectPrefix.length);

                // Handle .keep files
                if (relativePath.endsWith('/.keep')) {
                    const folderPath = relativePath.substring(0, relativePath.lastIndexOf('/.keep'));
                    if (folderPath && !processedFolders.has(`folder-${folderPath}`)) {
                        processedFolders.add(`folder-${folderPath}`);
                        return {
                            name: `${this.projectId}/src/frontend/public/assets/${folderPath}`,
                            content: '',
                            id: `folder-${folderPath}`,
                            path: `${this.projectId}/src/frontend/public/assets`,
                            fileName: folderPath,
                            fileSystem: 'asset' as const,
                            mimeType: 'folder',
                            size: 0,
                            displayIcon: '📁',
                            displayColor: '#FF9800',
                            tooltipSuffix: ' (Asset Folder)'
                        };
                    }
                    return null;
                }

                // Handle regular files - only fetch details if needed
                try {
                    const assetDetails = await this.assetManager.get(key);
                    if (!assetDetails) {
                        return null;
                    }

                    const pathParts = relativePath.split('/');
                    const fileName = pathParts[pathParts.length - 1];

                    let url = '';
                    if (host.includes('localhost') || host.includes('127.0.0.1')) {
                        url = `http://${this.frontendCanisterId.toText()}.localhost:4943/${cleanKey}`;
                    } else {
                        url = `https://${this.frontendCanisterId.toText()}.raw.icp0.io/${cleanKey}`;
                    }

                    const virtualPath = `${this.projectId}/src/frontend/public/assets/${relativePath}`;
                    const parentPath = relativePath.includes('/')
                        ? `${this.projectId}/src/frontend/public/assets/${relativePath.substring(0, relativePath.lastIndexOf('/'))}`
                        : `${this.projectId}/src/frontend/public/assets`;

                    return {
                        name: virtualPath,
                        content: url,
                        id: `asset-${fileName}-${Date.now()}-${Math.random()}`,
                        path: parentPath,
                        fileName,
                        fileSystem: 'asset' as const,
                        assetKey: key,
                        assetUrl: url,
                        mimeType: assetDetails.contentType || 'application/octet-stream',
                        size: typeof assetDetails.length === 'bigint'
                            ? Number(assetDetails.length)
                            : Number(assetDetails.length || 0),
                        displayIcon: this.getFileIcon(assetDetails.contentType || ''),
                        displayColor: '#2196F3',
                        tooltipSuffix: ' (Asset Canister)'
                    };
                } catch (error) {
                    console.warn(`❌ Failed to process asset ${key}:`, error);
                    return null;
                }
            });

            const batchResults = await Promise.all(batchPromises);
            // Filter out nulls and cast to ProjectFile[] - objects have all required properties
            for (const result of batchResults) {
                if (result !== null) {
                    assetFiles.push(result as ProjectFile);
                }
            }
        }

        // console.log('\n=== ASSET FETCH SUMMARY ===');
        // console.log('Total assets processed:', assetList.length);
        // console.log('Virtual folders created:', processedFolders.size);
        // console.log('Total asset files created:', assetFiles.length);
        // console.log('Processed folders:', Array.from(processedFolders));

        // assetFiles.forEach((file, index) => {
        //     console.log(`Asset file ${index + 1}:`, {
        //         name: file.name,
        //         fileName: file.fileName,
        //         fileSystem: file.fileSystem,
        //         mimeType: file.mimeType
        //     });
        // });

        return assetFiles;
    }


    async deleteAsset(assetKey: string): Promise<void> {
        if (!this.assetManager) {
            throw new Error('Asset manager not initialized');
        }
        await this.assetManager.delete(assetKey);
    }

    // NEW: Delete entire folder and all its contents
    async deleteFolder(folderPath: string): Promise<void> {
        if (!this.assetManager) {
            throw new Error('Asset manager not initialized');
        }

        const assetList = await this.assetManager.list();
        const projectPrefix = `projects/${this.projectId}/`;
        const targetPrefix = `${projectPrefix}${folderPath}/`;

        const assetsToDelete: string[] = [];

        for (const asset of assetList) {
            let key = "";
            if (typeof asset === 'string') {
                key = asset;
            } else if (asset && typeof asset === 'object' && 'key' in asset) {
                key = asset.key as string;
            }

            const cleanKey = key.startsWith('/') ? key.substring(1) : key;

            // If this asset is in the folder we want to delete
            if (cleanKey.startsWith(targetPrefix)) {
                assetsToDelete.push(key);
            }
        }

        // Delete all assets in the folder
        for (const assetKey of assetsToDelete) {
            try {
                await this.assetManager.delete(assetKey);
                console.log(`Deleted asset: ${assetKey}`);
            } catch (error) {
                console.warn(`Failed to delete asset ${assetKey}:`, error);
            }
        }
    }

    async createAsset(fileName: string, content: Uint8Array, folderPath?: string): Promise<void> {
        if (!this.assetManager) {
            throw new Error('Asset manager not initialized');
        }

        const projectPrefix = `projects/${this.projectId}/`;
        const assetPath = folderPath
            ? `${projectPrefix}${folderPath}/${fileName}`
            : `${projectPrefix}${fileName}`;

        const contentType = this.getContentTypeFromExtension(fileName);

        await this.assetManager.store(content, {
            fileName: assetPath,
            contentType
        });
    }

    async createFolder(folderName: string, parentPath?: string): Promise<void> {
        if (!this.assetManager) {
            throw new Error('Asset manager not initialized');
        }

        const projectPrefix = `projects/${this.projectId}/`;
        const folderPath = parentPath
            ? `${projectPrefix}${parentPath}/${folderName}`
            : `${projectPrefix}${folderName}`;

        // Create .keep file to establish the folder
        await this.assetManager.store(new Uint8Array(0), {
            fileName: `${folderPath}/.keep`,
            contentType: 'application/octet-stream'
        });
    }

    private getFileIcon(contentType: string): string {
        if (contentType.startsWith('image/')) return '🖼️';
        if (contentType.startsWith('video/')) return '🎬';
        if (contentType.startsWith('audio/')) return '🎵';
        if (contentType.startsWith('text/')) return '📄';
        if (contentType.includes('pdf')) return '📋';
        return '📎';
    }

    private getContentTypeFromExtension(fileName: string): string {
        const ext = fileName.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'png':
                return 'image/png';
            case 'jpg':
            case 'jpeg':
                return 'image/jpeg';
            case 'gif':
                return 'image/gif';
            case 'svg':
                return 'image/svg+xml';
            case 'webp':
                return 'image/webp';
            case 'mp4':
                return 'video/mp4';
            case 'mp3':
                return 'audio/mpeg';
            case 'pdf':
                return 'application/pdf';
            case 'css':
                return 'text/css';
            case 'js':
                return 'text/javascript';
            case 'html':
                return 'text/html';
            default:
                return 'application/octet-stream';
        }
    }
}

class DualFileSystemManager {
    private assetService: AssetCanisterService | null = null;
    private systemCanisters: any;
    private userPrincipal: Principal;

    constructor(
        systemCanisters: any,
        userPrincipal: Principal,
        frontendCanisterId?: Principal,
        identity?: any,
        projectId?: string
    ) {
        this.systemCanisters = systemCanisters;
        this.userPrincipal = userPrincipal;

        if (frontendCanisterId && identity && projectId) {
            this.assetService = new AssetCanisterService(frontendCanisterId, identity, projectId);
        }
    }

    async initialize(): Promise<void> {
        if (this.assetService) {
            await this.assetService.initialize();
        }
    }

    // Delete method - only handles backend code artifacts
    async deleteFile(file: ProjectFile, projectId: string): Promise<void> {
        if (file.fileSystem === 'asset') {
            throw new Error('Asset files cannot be deleted from sidebar. Use Asset Manager tab.');
        } else {
            throw new Error('This method should only be used for asset files. Backend/frontend files should use onDeleteFile prop.');
        }
    }

    // NEW: Delete folder method
    async deleteFolder(folderPath: string, fileSystem: 'backend' | 'frontend' | 'asset'): Promise<void> {
        if (fileSystem === 'asset') {
            if (!this.assetService) {
                throw new Error('Cannot delete asset folder: asset service not available');
            }

            // Extract the relative path from the virtual folder path
            const projectId = this.assetService.getProjectId();
            const assetBasePath = `${projectId}/src/frontend/src/assets/`;

            if (folderPath.startsWith(assetBasePath)) {
                const relativePath = folderPath.substring(assetBasePath.length);
                await this.assetService.deleteFolder(relativePath);
            } else {
                throw new Error('Invalid asset folder path');
            }
        } else {
            // For backend/frontend folders, we need to delete all files in the folder
            // This would need to be implemented based on your backend API
            throw new Error('Backend/frontend folder deletion not implemented yet');
        }
    }

    async getAllFiles(backendFiles: ProjectFile[]): Promise<ProjectFile[]> {
        console.log('=== DUAL FILE SYSTEM DEBUG START ===');

        // Convert backend files to include file system info
        const enhancedBackendFiles: ProjectFile[] = backendFiles
            .filter(file => {
                // FIXED: Filter out .keep files completely
                return !file.name.endsWith('/.keep') && file.fileName !== '.keep';
            })
            .map(file => {
                const path = file.name.toLowerCase();
                let fileSystemType: 'backend' | 'frontend' = 'backend';
                let displayColor = '#5C4033';
                let displayIcon = '💾';
                let tooltipSuffix = ' (Backend)';

                // Check if this is a regular frontend code file
                if (path.includes('/frontend/') ||
                    (!path.endsWith('.mo') && !path.endsWith('.toml') &&
                        ['.js', '.jsx', '.ts', '.tsx', '.css', '.html', '.svg', '.json'].some(ext => path.endsWith(ext)))) {
                    fileSystemType = 'frontend';
                    displayColor = '#2196F3';
                    displayIcon = '🌐';
                    tooltipSuffix = ' (Frontend)';
                }

                return {
                    ...file,
                    fileSystem: fileSystemType,  // Will be 'backend' or 'frontend'
                    displayIcon,
                    displayColor,
                    tooltipSuffix
                };
            });

        console.log('Enhanced backend files:', enhancedBackendFiles.length);

        // FIXED: Create virtual folders for directories that only had .keep files
        const foldersFromKeepFiles = new Set<string>();

        // Find all .keep files to determine which folders should exist
        backendFiles.forEach(file => {
            if (file.name.endsWith('/.keep')) {
                const folderPath = file.name.substring(0, file.name.lastIndexOf('/.keep'));
                foldersFromKeepFiles.add(folderPath);
            }
        });

        console.log('Folders from .keep files:', Array.from(foldersFromKeepFiles));

        // Add virtual folder entries for folders that only have .keep files
        foldersFromKeepFiles.forEach(folderPath => {
            const folderName = folderPath.substring(folderPath.lastIndexOf('/') + 1);
            const parentPath = folderPath.substring(0, folderPath.lastIndexOf('/'));

            // Determine if this is a frontend or backend folder
            let fileSystemType: 'backend' | 'frontend' = 'backend';
            let displayColor = '#5C4033';
            let tooltipSuffix = ' (Backend Folder)';

            if (folderPath.includes('/frontend/')) {
                fileSystemType = 'frontend';
                displayColor = '#2196F3';
                tooltipSuffix = ' (Frontend Folder)';
            }

            enhancedBackendFiles.push({
                name: folderPath,
                content: '',
                id: `virtual-folder-${folderPath}`,
                path: parentPath || '',
                fileName: folderName,
                fileSystem: fileSystemType,
                mimeType: 'folder',
                size: 0,
                displayIcon: '📁',
                displayColor,
                tooltipSuffix
            });
        });

        // Get asset files if available
        let assetFiles: ProjectFile[] = [];
        if (this.assetService) {
            try {
                const rawAssetFiles = await this.assetService.fetchAllAssetFiles();
                const correctProjectName = this.extractProjectNameFromBackendFiles(enhancedBackendFiles);

                // Remap asset files and ensure they have 'asset' fileSystem type
                assetFiles = rawAssetFiles.map(assetFile => {
                    const assetPath = assetFile.name;
                    const assetPathParts = assetPath.split('/');
                    const projectId = this.assetService?.getProjectId();

                    if (projectId && assetPathParts[0] === projectId) {
                        assetPathParts[0] = correctProjectName;
                        const newName = assetPathParts.join('/');

                        const pathParts = assetFile.path?.split('/') || [];
                        if (pathParts[0] === projectId) {
                            pathParts[0] = correctProjectName;
                        }
                        const newPath = pathParts.join('/');

                        const remappedFile = {
                            ...assetFile,
                            name: newName,
                            path: newPath,
                            fileSystem: 'asset' as const  // Explicitly set to 'asset'
                        };

                        return remappedFile;
                    }

                    return {
                        ...assetFile,
                        fileSystem: 'asset' as const  // Explicitly set to 'asset'
                    };
                });

            } catch (error) {
                console.warn('Failed to fetch asset files:', error);
            }
        } else {
            console.log('No asset service available');
        }

        return [...enhancedBackendFiles, ...assetFiles];
        ;
    }

    private extractProjectNameFromBackendFiles(backendFiles: ProjectFile[]): string {
        // Find the project name by looking at the backend file paths
        // Backend files typically start with the project name
        for (const file of backendFiles) {
            if (file.name && !file.name.startsWith('src/')) {
                // Extract the first part of the path as project name
                const pathParts = file.name.split('/');
                if (pathParts.length > 0 && pathParts[0]) {
                    return pathParts[0];
                }
            }
        }

        // Fallback to asset service project ID if we can't determine from backend files
        return this.assetService?.getProjectId() || 'project';
    }

    // File/folder creation - only for backend code artifacts
    async createFile(
        fileName: string,
        content: string,
        projectId: string,
        filePath: string
    ): Promise<void> {
        // All file creation goes through backend, even frontend files
        const mimeType = this.getMimeTypeFromExtension(fileName);
        const language = this.getLanguageFromExtension(fileName);

        const result = await this.systemCanisters.userActor.createCodeArtifact(
            this.userPrincipal,
            projectId,
            fileName,
            {Text: content},
            mimeType,
            language,
            filePath,
            []
        );

        if ('err' in result) {
            throw new Error(`Failed to create file: ${result.err}`);
        }
    }

    async createFolder(
        folderName: string,
        parentPath: string
    ): Promise<void> {
        // All folder creation goes through backend with .keep files
        const targetPath = parentPath ? `${parentPath}/${folderName}` : folderName;
        const keepFileName = '.keep';
        const content = {Text: ''};

        const result = await this.systemCanisters.userActor.createCodeArtifact(
            this.userPrincipal,
            '', // Will be filled by the calling context
            keepFileName,
            content,
            'text/plain',
            'plaintext',
            targetPath,
            []
        );

        if ('err' in result) {
            throw new Error(`Failed to create folder: ${result.err}`);
        }
    }

    private getMimeTypeFromExtension(fileName: string): string {
        const ext = fileName.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'mo':
                return 'text/x-motoko';
            case 'ts':
            case 'tsx':
                return 'text/typescript';
            case 'js':
            case 'jsx':
                return 'text/javascript';
            case 'css':
                return 'text/css';
            case 'html':
                return 'text/html';
            case 'json':
                return 'application/json';
            default:
                return 'text/plain';
        }
    }

    private getLanguageFromExtension(fileName: string): string {
        const ext = fileName.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'mo':
                return 'motoko';
            case 'ts':
            case 'tsx':
                return 'typescript';
            case 'js':
            case 'jsx':
                return 'javascript';
            case 'css':
                return 'css';
            case 'html':
                return 'html';
            case 'json':
                return 'json';
            default:
                return 'plaintext';
        }
    }
}


const CollapseButton: React.FC<{
    isCollapsed: boolean;
    onClick: () => void;
}> = ({isCollapsed, onClick}) => (
    <button
        onClick={onClick}
        style={{
            background: "none",
            border: "none",
            color: "#5C4033",
            cursor: "pointer",
            fontSize: "12px",
            padding: "0 4px",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "20px",
            width: "20px",
            transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease"
        }}
        title={isCollapsed ? "Expand section" : "Collapse section"}
    >
        ▼
    </button>
);


interface SidebarProps {
    files: ProjectFile[];
    motokoPackages: any[];
    npmPackages: any[];
    canisters: any[];
    onFileClick: (path: string) => void;
    activeFile: string;
    activeType: 'frontend' | 'backend';
    onTypeChange: (type: 'frontend' | 'backend') => void;
    isMotokoPackagesDialogOpen: boolean;
    setIsMotokoPackagesDialogOpen: (open: boolean) => void;
    isNPMPackagesDialogOpen: boolean;
    setIsNPMPackagesDialogOpen: (open: boolean) => void;
    onNewFile: () => void;
    onNewFolder: () => void;
    onDeleteFile: (filePath: string) => void;
    loading: boolean;
    project: any;
    children?: React.ReactNode;
    className?: string;
    systemCanisters: any;
    userPrincipal: Principal;
    frontendCanisterId?: Principal;
    identity?: any;
    assetRefreshTrigger?: number;
    onFileSystemChange?: () => void;
    onSuccess?: (message: string) => void;
    onError?: (error: string) => void;
    refreshUserCanisters?: () => Promise<void>;
    refreshUserProjects?: () => Promise<void>;
    onAddCanisterToProject?: (
        projectId: string,
        canisterId: string,
        onSuccess?: (message: string) => void,
        onError?: (message: string) => void
    ) => Promise<boolean>;
    onRemoveCanisterFromProject?: (
        projectId: string,
        canisterId: Principal,
        onSuccess?: (message: string) => void,
        onError?: (message: string) => void
    ) => Promise<boolean>;
}

export const Sidebar: React.FC<SidebarProps> = ({
                                                    files,
                                                    project,
                                                    systemCanisters,
                                                    userPrincipal,
                                                    frontendCanisterId,
                                                    identity,
                                                    onFileClick,
                                                    onDeleteFile,
                                                    activeFile,
                                                    activeType,
                                                    loading,
                                                    assetRefreshTrigger,
                                                    onSuccess,
                                                    onError,
                                                    onAddCanisterToProject,
                                                    onRemoveCanisterFromProject,
                                                    refreshUserCanisters,
                                                    refreshUserProjects,
                                                    ...otherProps
                                                }) => {
    const [dualFileManager, setDualFileManager] = useState<DualFileSystemManager | null>(null);
    const [fileTree, setFileTree] = useState<FileTreeNode | null>(null);
    const [allFiles, setAllFiles] = useState<ProjectFile[]>([]);
    const [isLoadingAssets, setIsLoadingAssets] = useState(false);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [pendingOperations, setPendingOperations] = useState<{
        adding: Set<string>;
        removing: Set<string>;
    }>({
        adding: new Set(),
        removing: new Set()
    });


    // NEW: States for folder operations and dropdown
    const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
    const [showFolderDialog, setShowFolderDialog] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderPath, setNewFolderPath] = useState<string | null>(null);
    const [useCustomFolderPath, setUseCustomFolderPath] = useState(false);
    const [showDeleteFolderConfirm, setShowDeleteFolderConfirm] = useState(false);
    const [showNewItemDropdown, setShowNewItemDropdown] = useState(false);
    const [isFilesCollapsed, setIsFilesCollapsed] = useState(false);
    const [isPackagesCollapsed, setIsPackagesCollapsed] = useState(false);
    const [isCanistersCollapsed, setIsCanistersCollapsed] = useState(false);
    const [fileSearchQuery, setFileSearchQuery] = useState('');
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    const [showAddCanisterDialog, setShowAddCanisterDialog] = useState(false);
    const [availableCanisters, setAvailableCanisters] = useState<any[]>([]);
    const [selectedCanisterToAdd, setSelectedCanisterToAdd] = useState<string>('');
    const [isLoadingCanisters, setIsLoadingCanisters] = useState(false);

    // Initialize the dual file system manager
    useEffect(() => {
        if (systemCanisters && userPrincipal) {
            const manager = new DualFileSystemManager(
                systemCanisters,
                userPrincipal,
                frontendCanisterId,
                identity,
                project?.id
            );
            setDualFileManager(manager);
        }
    }, [systemCanisters, userPrincipal, frontendCanisterId, identity, project?.id]);


    // Use refs to track previous state and prevent cascading reloads
    const previousFilesRef = React.useRef<string>('');
    const isReloadingRef = React.useRef(false);

    const loadAllFiles = useCallback(async () => {
        if (!dualFileManager || !project?.id) return;

        // Prevent cascading: check if we're already reloading
        if (isReloadingRef.current) {
            console.log('[Sidebar] Already reloading files, skipping duplicate call');
            return;
        }

        // Use the ref to get current files without causing dependency changes
        const currentFiles = filesRef.current;

        // Create a stable signature of files to detect meaningful changes
        // Handle BigInt serialization by converting to string
        const filesSignature = JSON.stringify(
            currentFiles.map(f => {
                let idValue: string | undefined = undefined;
                if (f.id !== undefined) {
                    // Handle BigInt values that might come from IC canister (even though typed as string)
                    const id = f.id as any;
                    if (typeof id === 'bigint') {
                        idValue = id.toString();
                    } else {
                        idValue = String(id);
                    }
                }
                return { name: f.name, id: idValue };
            }).sort((a, b) => a.name.localeCompare(b.name)),
            (key, value) => {
                // Convert BigInt to string for JSON serialization
                if (typeof value === 'bigint') {
                    return value.toString();
                }
                return value;
            }
        );

        // Prevent cascading: only reload if files actually changed
        if (previousFilesRef.current === filesSignature) {
            console.log('[Sidebar] Files unchanged, skipping reload');
            return;
        }

        previousFilesRef.current = filesSignature;
        isReloadingRef.current = true;
        setIsLoadingFiles(true);

        try {
            
            // STEP 1: IMMEDIATELY show backend/frontend files without waiting for assets
            const enhancedBackendFiles: ProjectFile[] = currentFiles
            .filter(file => {
                // Filter out .keep files completely
                return !file.name.endsWith('/.keep') && file.fileName !== '.keep';
            })
            .map(file => {
                const path = file.name.toLowerCase();
                let fileSystemType: 'backend' | 'frontend' = 'backend';
                let displayColor = '#5C4033';
                let displayIcon = '💾';
                let tooltipSuffix = ' (Backend)';

                // Check if this is a regular frontend code file
                if (path.includes('/frontend/') ||
                    (!path.endsWith('.mo') && !path.endsWith('.toml') &&
                        ['.js', '.jsx', '.ts', '.tsx', '.css', '.html', '.svg', '.json'].some(ext => path.endsWith(ext)))) {
                    fileSystemType = 'frontend';
                    displayColor = '#2196F3';
                    displayIcon = '🌐';
                    tooltipSuffix = ' (Frontend)';
                }

                return {
                    ...file,
                    fileSystem: fileSystemType,
                    displayIcon,
                    displayColor,
                    tooltipSuffix
                };
            });

        // Create virtual folders for directories that only had .keep files
        const foldersFromKeepFiles = new Set<string>();
        currentFiles.forEach(file => {
            if (file.name.endsWith('/.keep')) {
                const folderPath = file.name.substring(0, file.name.lastIndexOf('/.keep'));
                foldersFromKeepFiles.add(folderPath);
            }
        });

        // Add virtual folder entries for folders that only have .keep files
        foldersFromKeepFiles.forEach(folderPath => {
            const folderName = folderPath.substring(folderPath.lastIndexOf('/') + 1);
            const parentPath = folderPath.substring(0, folderPath.lastIndexOf('/'));

            let fileSystemType: 'backend' | 'frontend' = 'backend';
            let displayColor = '#5C4033';
            let tooltipSuffix = ' (Backend Folder)';

            if (folderPath.includes('/frontend/')) {
                fileSystemType = 'frontend';
                displayColor = '#2196F3';
                tooltipSuffix = ' (Frontend Folder)';
            }

            enhancedBackendFiles.push({
                name: folderPath,
                content: '',
                id: `virtual-folder-${folderPath}`,
                path: parentPath || '',
                fileName: folderName,
                fileSystem: fileSystemType,
                mimeType: 'folder',
                size: 0,
                displayIcon: '📁',
                displayColor,
                tooltipSuffix
            });
        });

        // IMMEDIATELY set the file tree with backend/frontend files
        setAllFiles(enhancedBackendFiles);
        setFileTree(buildFileTree(enhancedBackendFiles));

        // STEP 2: Load assets in the background (don't block the UI)
        if (dualFileManager) {
            setIsLoadingAssets(true);

            // Use requestIdleCallback or setTimeout for non-blocking asset loading
            const loadAssetsAsync = async () => {
                try {
                    // Initialize in parallel with other operations
                    const initPromise = dualFileManager.initialize();
                    
                    // Use requestIdleCallback if available, otherwise setTimeout
                    if (typeof requestIdleCallback !== 'undefined') {
                        requestIdleCallback(async () => {
                            await initPromise;
                            await processAssets();
                        }, { timeout: 1000 });
                    } else {
                        setTimeout(async () => {
                            await initPromise;
                            await processAssets();
                        }, 0);
                    }
                } catch (error) {
                    console.warn('Failed to initialize asset manager:', error);
                    setIsLoadingAssets(false);
                }
            };

            const processAssets = async () => {
                try {
                    // Use the existing getAllFiles method but just extract the asset files
                    const allCombinedFiles = await dualFileManager.getAllFiles(currentFiles);

                    // Filter out just the asset files from the combined result
                    const assetFiles = allCombinedFiles.filter(file => file.fileSystem === 'asset');

                    if (assetFiles.length > 0) {
                        // STEP 3: Update with combined files (backend + frontend + assets)
                        const combinedFiles = [...enhancedBackendFiles, ...assetFiles];
                        setAllFiles(combinedFiles);
                        setFileTree(buildFileTree(combinedFiles));

                        console.log(`✅ Assets loaded: ${assetFiles.length} files added to tree`);
                    } else {
                        console.log('ℹ️ No assets found for this project');
                    }
                } catch (error) {
                    console.warn('Failed to load assets (continuing with backend/frontend files only):', error);
                    // Don't change the file tree - just keep the backend/frontend files that are already showing
                } finally {
                    setIsLoadingAssets(false);
                }
            };

            loadAssetsAsync();
        }
        } finally {
            isReloadingRef.current = false;
            setIsLoadingFiles(false);
        }
    }, [dualFileManager, project?.id]); // Removed 'files' from dependencies to prevent cascading


    // Reload files when project changes or files are explicitly updated
    // Use a ref to track files separately to avoid cascading
    const filesRef = React.useRef(files);
    React.useEffect(() => {
        const filesChanged = filesRef.current !== files;
        filesRef.current = files;
        
        // If files changed, trigger a reload (but loadAllFiles will check for duplicates)
        if (filesChanged) {
            // Small delay to batch multiple file updates
            const timeoutId = setTimeout(() => {
                loadAllFiles();
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [files, loadAllFiles]);

    // Reload when project or dualFileManager changes
    useEffect(() => {
        // Reset the previous files signature when project changes
        previousFilesRef.current = '';
        loadAllFiles();
    }, [loadAllFiles, project?.id, dualFileManager]);

    // Watch for asset refresh trigger and reload assets when frontend tab is active
    useEffect(() => {
        if (assetRefreshTrigger && activeType === 'frontend' && dualFileManager) {
            console.log('Asset refresh trigger detected, reloading assets...');
            loadAllFiles();
        }
    }, [assetRefreshTrigger, activeType, dualFileManager, loadAllFiles]);

    // NEW: Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showNewItemDropdown) {
                const target = event.target as HTMLElement;
                if (!target.closest('[data-dropdown-container]')) {
                    setShowNewItemDropdown(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNewItemDropdown]);

    const fetchAvailableCanisters = useCallback(async () => {
        try {
            if (systemCanisters?.userActor && userPrincipal) {
                setIsLoadingCanisters(true);
                const canisters = await systemCanisters.userActor.getUserCanisters();

                // Filter out canisters that are already in the project
                const projectCanisterIds = new Set(
                    project?.canisters?.map((c: Principal) => c.toText()) || []
                );

                const availableForProject = canisters.filter((canister: any) =>
                    !projectCanisterIds.has(canister.principal.toText())
                );

                setAvailableCanisters(availableForProject);
            }
        } catch (error) {
            console.error('Failed to fetch available canisters:', error);
            onError?.('Failed to fetch available canisters');
        } finally {
            setIsLoadingCanisters(false);
        }
    }, [systemCanisters?.userActor, userPrincipal, project?.canisters, onError]);


    const handleAddCanisterToProject = async () => {
        if (!selectedCanisterToAdd || !project?.id || !onAddCanisterToProject) {
            return;
        }

        // Set loading state
        setPendingOperations(prev => ({
            ...prev,
            adding: new Set([...prev.adding, selectedCanisterToAdd])
        }));

        // Close dialog immediately for better UX
        setShowAddCanisterDialog(false);
        const canisterToAdd = selectedCanisterToAdd;
        setSelectedCanisterToAdd('');

        try {
            // Call the parent handler which manages all the optimistic updates
            const success = await onAddCanisterToProject(
                project.id,
                canisterToAdd,
                onSuccess,
                onError
            );

            if (success) {
                // Refresh available canisters list
                await fetchAvailableCanisters();
            }
        } finally {
            // Clear loading state
            setPendingOperations(prev => ({
                ...prev,
                adding: new Set([...prev.adding].filter(id => id !== canisterToAdd))
            }));
        }
    };

    const handleRemoveCanisterFromProject = async (canisterId: Principal) => {
        if (!project?.id || !onRemoveCanisterFromProject) {
            return;
        }

        // Show confirmation
        if (!window.confirm('Are you sure you want to remove this canister from the project?')) {
            return;
        }

        const canisterIdText = canisterId.toText();

        // Set loading state
        setPendingOperations(prev => ({
            ...prev,
            removing: new Set([...prev.removing, canisterIdText])
        }));

        try {
            // Call the parent handler which manages all the optimistic updates
            const success = await onRemoveCanisterFromProject(
                project.id,
                canisterId,
                onSuccess,
                onError
            );

            if (success) {
                // Refresh available canisters list
                await fetchAvailableCanisters();
            }
        } finally {
            // Clear loading state
            setPendingOperations(prev => ({
                ...prev,
                removing: new Set([...prev.removing].filter(id => id !== canisterIdText))
            }));
        }
    };

    useEffect(() => {
        if (showAddCanisterDialog || project?.id) {
            fetchAvailableCanisters();
        }
    }, [showAddCanisterDialog, project?.id, fetchAvailableCanisters]);


    // Enhanced delete handler - routes to backend only, protects assets
    const handleDeleteFile = async (filePath: string) => {
        try {
            // Find the file in our combined file list to check its type
            const fileToDelete = allFiles.find(f => f.name === filePath);

            if (!fileToDelete) {
                console.error('File not found in allFiles:', filePath);
                // Fallback to existing delete method
                onDeleteFile(filePath);
                return;
            }

            console.log('File to delete:', fileToDelete);

            if (fileToDelete.fileSystem === 'asset') {
                // Prevent deletion of asset files from sidebar
                alert('Asset files cannot be deleted from here. Use the Asset Manager tab to manage asset files.');
                return;
            } else {
                // For backend/frontend code files, use the existing delete method
                console.log('Deleting backend code file:', filePath);
                onDeleteFile(filePath);
            }

        } catch (error) {
            console.error('Failed to delete file:', error);
            // Fallback to the original delete method
            onDeleteFile(filePath);
        }
    };

// FIXED: Delete ALL files in the folder, not just .keep file
    const handleDeleteFolder = async () => {
        if (!selectedFolder) return;

        try {
            // Check if this is the assets folder - prevent deletion
            if (selectedFolder.includes('/frontend/public/assets')) {
                alert('Asset folders cannot be deleted from here. Use the Asset Manager tab to manage asset files.');
                setShowDeleteFolderConfirm(false);
                setSelectedFolder(null);
                return;
            }

            console.log('Attempting to delete entire folder:', selectedFolder);
            // FIXED: Find ALL files in the folder from the original files array
            const folderFiles = files.filter(file => {
                // Check if file is in this folder or its subfolders
                return file.name.startsWith(selectedFolder + '/') &&
                    file.fileSystem !== 'asset'; // Don't delete asset files from sidebar
            });

            console.log('Files found in folder to delete:', folderFiles.map(f => f.name));

            if (folderFiles.length === 0) {
                console.warn('No files found in folder to delete:', selectedFolder);
                // Try to delete just the .keep file as fallback
                const keepFilePath = `${selectedFolder}/.keep`;
                try {
                    await onDeleteFile(keepFilePath);
                    console.log('Deleted .keep file as fallback:', keepFilePath);
                } catch (error) {
                    throw new Error(`No files found in folder and .keep file deletion failed: ${(error as Error).message}`);
                }
            } else {
                // Delete each file in the folder
                let deleteCount = 0;
                let errorCount = 0;

                for (const file of folderFiles) {
                    try {
                        console.log('Deleting file:', file.name);
                        await onDeleteFile(file.name);
                        deleteCount++;
                    } catch (error) {
                        console.error(`Failed to delete file ${file.name}:`, error);
                        errorCount++;
                    }
                }

                console.log(`Deletion complete: ${deleteCount} files deleted, ${errorCount} errors`);

                if (errorCount > 0 && deleteCount === 0) {
                    throw new Error(`Failed to delete all ${folderFiles.length} files in the folder`);
                }
            }

            // Reload the file tree
            await loadAllFiles();

            setShowDeleteFolderConfirm(false);
            setSelectedFolder(null);
            console.log('Folder deletion completed successfully:', selectedFolder);

        } catch (error) {
            console.error('Failed to delete folder:', error);
            alert(`Failed to delete folder: ${(error as Error).message}`);
            setShowDeleteFolderConfirm(false);
            setSelectedFolder(null);
        }
    };

    // Generate directory options from file tree (similar to your file creation)
    const generateDirectoryOptions = (tree: FileTreeNode | null): { path: string }[] => {
        if (!tree) return [];

        const options: { path: string }[] = [];

        const traverse = (node: FileTreeNode) => {
            if (node.isDirectory) {
                // Only include backend/frontend directories, exclude asset folders
                if (!node.path.includes('/frontend/public/assets') &&
                    !node.path.includes('/frontend/src/assets')) {
                    options.push({path: node.path});
                }

                if (node.children) {
                    node.children.forEach(child => traverse(child));
                }
            }
        };

        traverse(tree);
        return options.sort((a, b) => a.path.localeCompare(b.path));
    };

    // Handle folder creation - FIXED path combination
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;

        try {
            // All folder creation goes through backend - even for frontend folders
            if (!systemCanisters?.userActor || !userPrincipal || !project?.id) {
                throw new Error('Missing required dependencies for folder creation');
            }

            // FIXED: Always combine path and folder name properly
            let targetPath = '';
            const parentPath = newFolderPath ||
                (activeType === 'backend' ? `${project?.name}/src/backend` : `${project?.name}/src/frontend/src`);

            targetPath = `${parentPath}/${newFolderName}`.replace(/\/\/+/g, '/');

            if (!targetPath) {
                throw new Error('Folder path is required');
            }

            const keepFileName = '.keep';
            const content = {Text: ''};

            console.log('[Sidebar] Creating backend folder:', {
                selectedPath: newFolderPath,
                folderName: newFolderName,
                combinedTargetPath: targetPath,
                keepFileName,
                projectId: project.id
            });

            const result = await systemCanisters.userActor.createCodeArtifact(
                userPrincipal,
                project.id,
                keepFileName,
                content,
                'text/plain',
                'plaintext',
                targetPath,
                [] // No version parameter for now
            );

            if ('err' in result) {
                throw new Error(result.err);
            }

            console.log('✅ Folder created successfully, refreshing...');

            // FIXED: Trigger parent to reload files
            if (otherProps.onFileSystemChange) {
                otherProps.onFileSystemChange();
            }

            // Also refresh the sidebar's internal state
            await loadAllFiles();

            // Reset dialog state
            setShowFolderDialog(false);
            setNewFolderName('');
            setNewFolderPath(null);
            setUseCustomFolderPath(false);

            console.log('✅ Folder creation and refresh completed');
        } catch (error) {
            console.error('Failed to create folder:', error);
            alert(`Failed to create folder: ${(error as Error).message}`);
        }
    };

    // Handle asset clicks (open in new tab)
    const handleAssetClick = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // Enhanced file click handler that routes correctly
    const handleFileClick = (filePath: string) => {
        const file = allFiles.find(f => f.name === filePath);

        if (file?.fileSystem === 'asset') {  // Only actual asset files open in new tab
            if (file.assetUrl) {
                handleAssetClick(file.assetUrl);
            }
        } else {
            // All other files (backend and frontend code files) open in editor
            onFileClick(filePath);
        }
    };

    // NEW: Handle folder selection for context menu
    const handleFolderRightClick = (folderPath: string, event: React.MouseEvent) => {
        event.preventDefault();
        setSelectedFolder(folderPath);
        setShowDeleteFolderConfirm(true);
    };

    const getCanisterLink = (canisterType: string, canisterId: string): string => {
        if (process.env.DFX_NETWORK === "ic") {
            // Production (Internet Computer mainnet)
            if (canisterType.toLowerCase() === 'backend') {
                const CANDID_UI_MAINNET = 'a4gq6-oaaaa-aaaab-qaa4q-cai';
                return `https://${CANDID_UI_MAINNET}.raw.icp0.io/?id=${canisterId}`;
            } else {
                // Frontend canister
                return `https://${canisterId}.icp0.io`;
            }
        } else {
            // Local development
            const host = "http://127.0.0.1:4943";
            const CANDID_UI = 'umunu-kh777-77774-qaaca-cai';

            if (canisterType.toLowerCase() === 'backend') {
                return `${host}?canisterId=${CANDID_UI}&id=${canisterId}`;
            } else {
                // Frontend canister
                return `${host}?canisterId=${canisterId}`;
            }
        }
    };


    return (
        <div
            className={otherProps.className || ''}
            style={{
                width: "250px",
                backgroundColor: "#fdf6e3",
                borderRight: "1px solid #8B4513",
                height: "100%",
                overflowY: "auto"
            }}
        >
            {/* Header with Dropdown */}
            <div style={{
                padding: "10px",
                borderBottom: "1px solid #8B4513",
                backgroundColor: "#fdf6e3",
            }}>
                <select
                    value={activeType}
                    onChange={(e) => otherProps.onTypeChange(e.target.value as 'frontend' | 'backend')}
                    style={{
                        width: "100%",
                        padding: "8px 12px",
                        marginBottom: "8px",
                        borderRadius: "4px",
                        backgroundColor: "#8B4513",
                        color: "#FFF5EE",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "16px"
                    }}
                >
                    <option value="backend">Backend Files</option>
                    <option value="frontend">Frontend Files</option>
                </select>
            </div>

            {/* FILES Section - Updated */}
            <div>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderBottom: "1px solid #8B4513",
                    backgroundColor: "rgba(139, 69, 19, 0.1)"
                }}>
                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                        <CollapseButton
                            isCollapsed={isFilesCollapsed}
                            onClick={() => setIsFilesCollapsed(!isFilesCollapsed)}
                        />
                        <span style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#5C4033"
                        }}>
                FILES {isLoadingAssets &&
                            <span style={{color: '#666', fontSize: '11px'}}>(loading assets...)</span>}
            </span>
                    </div>
                    <div style={{display: "flex", gap: "8px", position: "relative"}}>
                        <button
                            onClick={async () => {
                                // Force refresh files
                                if (otherProps.onFileSystemChange) {
                                    await otherProps.onFileSystemChange();
                                }

                                await loadAllFiles();
                            }}
                            disabled={loading || isLoadingAssets}
                            style={{
                                background: "none",
                                border: "none",
                                color: (loading || isLoadingAssets) ? "#ccc" : "#5C4033",
                                cursor: (loading || isLoadingAssets) ? "not-allowed" : "pointer",
                                fontSize: "16px",
                                padding: "0 2px",
                                lineHeight: "1",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "20px",
                                width: "20px",
                                transition: "transform 0.2s ease"  // Add smooth transition
                            }}
                            onMouseEnter={(e) => {
                                if (!loading && !isLoadingAssets) {
                                    e.currentTarget.style.transform = "rotate(180deg)";  // Spin on hover
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "rotate(0deg)";
                            }}
                            title="Refresh files"
                        >
                            {(loading || isLoadingAssets) ? "⏳" : "🔄"}
                        </button>
                        {activeFile && (
                            <button
                                onClick={() => handleDeleteFile(activeFile)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#DC2626",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    padding: "0 2px",
                                    lineHeight: "1",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "20px",
                                    width: "20px"
                                }}
                                title="Delete file"
                            >
                                −
                            </button>
                        )}
                        <div style={{position: "relative"}} data-dropdown-container>
                            <button
                                onClick={() => setShowNewItemDropdown(!showNewItemDropdown)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#5C4033",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    padding: "0 2px",
                                    lineHeight: "1",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "20px",
                                    width: "20px"
                                }}
                                title="Create new file or folder"
                            >
                                +
                            </button>
                            {/* Your existing dropdown code remains the same */}
                            {showNewItemDropdown && (
                                <div style={{
                                    position: "absolute",
                                    top: "100%",
                                    right: "0",
                                    backgroundColor: "#fdf6e3",
                                    border: "1px solid #8B4513",
                                    borderRadius: "4px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    zIndex: 1000,
                                    minWidth: "120px"
                                }}>
                                    <button
                                        onClick={() => {
                                            setShowNewItemDropdown(false);
                                            otherProps.onNewFile();
                                        }}
                                        style={{
                                            width: "100%",
                                            padding: "8px 12px",
                                            background: "none",
                                            border: "none",
                                            textAlign: "left",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            color: "#5C4033",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "rgba(139, 69, 19, 0.1)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                        }}
                                    >
                                        <span>📄</span>
                                        New File
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowNewItemDropdown(false);
                                            setShowFolderDialog(true);
                                        }}
                                        style={{
                                            width: "100%",
                                            padding: "8px 12px",
                                            background: "none",
                                            border: "none",
                                            textAlign: "left",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            color: "#5C4033",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "rgba(139, 69, 19, 0.1)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                        }}
                                    >
                                        <span>📁</span>
                                        New Folder
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {!isFilesCollapsed && (
                    <div style={{
                        padding: "4px 8px",
                        overflow: "hidden",
                        width: "100%",
                        boxSizing: "border-box"
                    }}>
                        {/* File Search Input */}
                        <div style={{
                            marginBottom: "8px",
                            padding: "0", // Remove padding to prevent overflow
                            width: "100%",
                            boxSizing: "border-box"
                        }}>
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search files (Ctrl+Shift+R)"
                                value={fileSearchQuery}
                                onChange={(e) => setFileSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setFileSearchQuery('');
                                        searchInputRef.current?.blur();
                                    }
                                }}
                                style={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    padding: "6px 8px",
                                    fontSize: "12px",
                                    border: "1px solid #8B4513",
                                    borderRadius: "4px",
                                    backgroundColor: "#fdf6e3",
                                    color: "#5C4033",
                                    outline: "none",
                                    boxSizing: "border-box" // Include padding in width calculation
                                }}
                            />
                        </div>
                        {(loading || isLoadingAssets || isLoadingFiles) ? (
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "20px",
                                color: "#5C4033"
                            }}>
                                <div style={{
                                    width: "32px",
                                    height: "32px",
                                    border: "3px solid rgba(139, 69, 19, 0.2)",
                                    borderTop: "3px solid #8B4513",
                                    borderRadius: "50%",
                                    animation: "spin 0.8s linear infinite",
                                    marginBottom: "12px"
                                }}></div>
                                <div style={{
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    color: "#5C4033",
                                    textAlign: "center"
                                }}>
                                    {isLoadingAssets 
                                        ? "Loading assets..." 
                                        : loading 
                                        ? "Downloading files..." 
                                        : "Loading files..."}
                                </div>
                                {(loading && !isLoadingAssets) && (
                                    <div style={{
                                        fontSize: "11px",
                                        color: "#8B4513",
                                        marginTop: "4px",
                                        opacity: 0.7
                                    }}>
                                        Fetching from backend...
                                    </div>
                                )}
                                <style>{`
                                    @keyframes spin {
                                        0% { transform: rotate(0deg); }
                                        100% { transform: rotate(360deg); }
                                    }
                                `}</style>
                            </div>
                        ) : (
                            fileTree && (() => {
                                const filteredTree = fileSearchQuery.trim() 
                                    ? filterFileTree(fileTree, fileSearchQuery)
                                    : fileTree;
                                
                                if (!filteredTree || (filteredTree.children && filteredTree.children.length === 0)) {
                                    return (
                                        <div style={{
                                            color: "#8B4513",
                                            fontSize: "12px",
                                            padding: "8px",
                                            textAlign: "center"
                                        }}>
                                            {fileSearchQuery.trim() ? 'No files found matching your search' : 'No files'}
                                        </div>
                                    );
                                }
                                
                                return (
                                    <div style={{
                                        width: "100%",
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                        boxSizing: "border-box"
                                    }}>
                                        <FileTreeView
                                            node={filteredTree}
                                            level={0}
                                            onFileClick={handleFileClick}
                                            onAssetClick={handleAssetClick}
                                            onFolderRightClick={handleFolderRightClick}
                                            activePath={activeFile}
                                            activeType={activeType}
                                            showFileSystemIndicators={true}
                                        />
                                    </div>
                                );
                            })()
                        )}
                    </div>
                )}
            </div>

            {/* PACKAGES Section - Updated */}
            <div>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderBottom: "1px solid #8B4513",
                    backgroundColor: "rgba(139, 69, 19, 0.1)"
                }}>
                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                        <CollapseButton
                            isCollapsed={isPackagesCollapsed}
                            onClick={() => setIsPackagesCollapsed(!isPackagesCollapsed)}
                        />
                        <span style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#5C4033"
                        }}>
                PACKAGES
            </span>
                    </div>
                    <button
                        onClick={() => activeType === 'backend'
                            ? otherProps.setIsMotokoPackagesDialogOpen(true)
                            : otherProps.setIsNPMPackagesDialogOpen(true)
                        }
                        style={{
                            background: "none",
                            border: "none",
                            color: "#5C4033",
                            cursor: "pointer",
                            fontSize: "16px",
                            padding: "0 2px",
                            lineHeight: "1"
                        }}
                    >
                        +
                    </button>
                </div>
                {!isPackagesCollapsed && (
                    <div style={{padding: "4px 8px"}}>
                        {activeType === 'backend' ? (
                            // Motoko Packages Section
                            otherProps.motokoPackages.length > 0 ? (
                                otherProps.motokoPackages.map((pkg, index) => (
                                    <div key={index} style={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "2px 0",
                                        fontSize: "16px"
                                    }}>
                                        <span style={{marginRight: "6px"}}>📦</span>
                                        <span>{pkg.name}</span>
                                    </div>
                                ))
                            ) : (
                                <div style={{
                                    color: "#8B4513",
                                    fontStyle: "italic",
                                    fontSize: "16px"
                                }}>
                                    No Motoko packages installed
                                </div>
                            )
                        ) : (
                            // NPM Packages Section
                            otherProps.npmPackages && otherProps.npmPackages.length > 0 ? (
                                otherProps.npmPackages.map((pkg, index) => (
                                    <div key={index} style={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "2px 0",
                                        fontSize: "16px"
                                    }}>
                                        <span style={{marginRight: "6px"}}>📦</span>
                                        <span>{pkg.name}</span>
                                        <span style={{
                                            marginLeft: "6px",
                                            fontSize: "12px",
                                            color: "#666",
                                            padding: "2px 6px",
                                            backgroundColor: "#f0f0f0",
                                            borderRadius: "4px"
                                        }}>
                                {pkg.dependencyType}
                            </span>
                                    </div>
                                ))
                            ) : (
                                <div style={{
                                    color: "#8B4513",
                                    fontStyle: "italic",
                                    fontSize: "16px"
                                }}>
                                    No NPM packages installed
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>

            {/* ENHANCED CANISTERS Section */}
            <div>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderBottom: "1px solid #8B4513",
                    backgroundColor: "rgba(139, 69, 19, 0.1)"
                }}>
                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                        <CollapseButton
                            isCollapsed={isCanistersCollapsed}
                            onClick={() => setIsCanistersCollapsed(!isCanistersCollapsed)}
                        />
                        <span style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#5C4033"
                        }}>
                            CANISTERS
                        </span>
                    </div>
                    {/* NEW: Add Canister Button */}
                    <button
                        onClick={() => setShowAddCanisterDialog(true)}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#5C4033",
                            cursor: "pointer",
                            fontSize: "16px",
                            padding: "0 2px",
                            lineHeight: "1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "20px",
                            width: "20px"
                        }}
                        title="Add canister to project"
                    >
                        +
                    </button>
                </div>
                {!isCanistersCollapsed && (
                    <div style={{padding: "4px 8px"}}>
                        {otherProps.canisters
                            .filter(canister =>
                                project?.canisters?.some(
                                    projectCanister => projectCanister.toText() === canister.principal.toText()
                                ) || false
                            )
                            .map((canister, index) => {
                                const canisterId = canister.principal.toText();
                                const isPendingAdd = pendingOperations.adding.has(canisterId);
                                const isPendingRemove = pendingOperations.removing.has(canisterId);

                                // Use the getCanisterLink function here
                                const canisterLink = getCanisterLink(canister.canisterType.toLowerCase(), canisterId);

                                const displayId = canisterId.length > 20
                                    ? `${canisterId.substring(0, 8)}...${canisterId.substring(canisterId.length - 6)}`
                                    : canisterId;

                                return (
                                    <div key={index} style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "6px 4px",
                                        fontSize: "13px",
                                        borderBottom: "1px solid rgba(139, 69, 19, 0.1)",
                                        marginBottom: "4px",
                                        backgroundColor: isPendingAdd || isPendingRemove ? "#fff3cd" : "#f9f5f0",
                                        borderRadius: "4px",
                                        border: `1px solid ${isPendingAdd || isPendingRemove ? "#ffc107" : "rgba(139, 69, 19, 0.2)"}`,
                                        opacity: isPendingRemove ? 0.6 : 1,
                                        transition: "all 0.2s ease"
                                    }}>
                                        <a
                                            href={canisterLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                color: "#5C4033",
                                                textDecoration: "none",
                                                flex: 1,
                                                minWidth: 0,
                                                gap: "6px"
                                            }}
                                            title={`${canister.canisterType} canister: ${canisterId}${isPendingAdd ? ' (syncing...)' : ''}${isPendingRemove ? ' (removing...)' : ''}`}
                                        >
                    <span style={{fontSize: "14px", minWidth: "16px"}}>
                        {canister.canisterType === 'frontend' ? '🌐' : '⚙️'}
                    </span>
                                            <div style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "2px",
                                                flex: 1,
                                                minWidth: 0
                                            }}>
                        <span style={{
                            fontSize: "11px",
                            color: "#666",
                            fontWeight: "500",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                        }}>
                            {canister.canisterType}
                            {isPendingAdd && <span style={{color: "#ffc107"}}> (syncing...)</span>}
                            {isPendingRemove && <span style={{color: "#dc2626"}}> (removing...)</span>}
                        </span>
                                                <span style={{
                                                    fontFamily: "monospace",
                                                    fontSize: "11px",
                                                    color: "#5C4033",
                                                    fontWeight: "500",
                                                    textDecoration: "underline",
                                                    textUnderlineOffset: "2px"
                                                }}>
                            {displayId}
                        </span>
                                            </div>
                                        </a>

                                        {/* Remove button with pending state */}
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleRemoveCanisterFromProject(canister.principal);
                                            }}
                                            disabled={isPendingRemove}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: isPendingRemove ? "#999" : "#dc2626",
                                                cursor: isPendingRemove ? "not-allowed" : "pointer",
                                                fontSize: "14px",
                                                padding: "4px 6px",
                                                borderRadius: "3px",
                                                marginLeft: "6px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                minWidth: "20px",
                                                height: "20px"
                                            }}
                                            title={isPendingRemove ? "Removing..." : "Remove canister from project"}
                                        >
                                            {isPendingRemove ? "⏳" : "×"}
                                        </button>
                                    </div>
                                );
                            })}
                        {/* Show message if no canisters */}
                        {(!otherProps.canisters || otherProps.canisters.filter(canister =>
                            project?.canisters?.some(
                                projectCanister => projectCanister.toText() === canister.principal.toText()
                            )
                        ).length === 0) && (
                            <div style={{
                                color: "#8B4513",
                                fontStyle: "italic",
                                fontSize: "13px",
                                textAlign: "center",
                                padding: "12px 8px",
                                backgroundColor: "#f9f5f0",
                                borderRadius: "4px",
                                border: "1px solid rgba(139, 69, 19, 0.2)"
                            }}>
                                No canisters in project
                                <br/>
                                <span style={{fontSize: "11px", color: "#666"}}>Click + to add one</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Enhanced Folder Creation Dialog */}
            {showFolderDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fdf6e3',
                        padding: '20px',
                        borderRadius: '8px',
                        width: '400px',
                        boxShadow: '0px 4px 6px rgba(0,0,0,0.1)'
                    }}>
                        <h3 style={{
                            margin: '0 0 15px 0',
                            color: '#5C4033',
                            fontSize: '18px',
                            fontWeight: '500'
                        }}>
                            Create New Folder
                        </h3>

                        <div style={{marginBottom: '15px'}}>
                            <label style={{
                                display: 'block',
                                marginBottom: '5px',
                                color: '#5C4033',
                                fontSize: '14px'
                            }}>
                                Parent Directory:
                            </label>

                            {/* Checkbox for toggling between dropdown and custom input */}
                            <div style={{marginBottom: '10px'}}>
                                <label style={{fontSize: '14px', color: '#5C4033'}}>
                                    <input
                                        type="checkbox"
                                        checked={useCustomFolderPath}
                                        onChange={() => setUseCustomFolderPath(!useCustomFolderPath)}
                                        style={{marginRight: '5px'}}
                                    />
                                    Create in custom directory path
                                </label>
                            </div>

                            {useCustomFolderPath ? (
                                <input
                                    type="text"
                                    value={newFolderPath || ''}
                                    onChange={(e) => setNewFolderPath(e.target.value)}
                                    placeholder={`${project?.name}/src/custom/path`}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        border: '1px solid #8B4513',
                                        backgroundColor: '#fff',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            ) : (
                                <select
                                    value={newFolderPath ||
                                        (activeType === 'backend' ? `${project?.name}/src/backend` : `${project?.name}/src/frontend/src`)}
                                    onChange={(e) => setNewFolderPath(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        border: '1px solid #8B4513',
                                        backgroundColor: '#fff',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    {generateDirectoryOptions(fileTree).map((dir, idx) => (
                                        <option key={idx} value={dir.path}>
                                            {dir.path}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div style={{marginBottom: '15px'}}>
                            <label style={{
                                display: 'block',
                                marginBottom: '5px',
                                color: '#5C4033',
                                fontSize: '14px'
                            }}>
                                Folder Name:
                            </label>
                            <input
                                type="text"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                placeholder="folder-name"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #8B4513',
                                    backgroundColor: '#fff',
                                    fontSize: '14px',
                                    boxSizing: 'border-box'
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && newFolderName.trim()) {
                                        handleCreateFolder();
                                    }
                                }}
                                autoFocus
                            />
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px'
                        }}>
                            <button
                                onClick={() => {
                                    setShowFolderDialog(false);
                                    setNewFolderName('');
                                    setNewFolderPath(null);
                                    setUseCustomFolderPath(false);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#8B4513',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateFolder}
                                disabled={!newFolderName.trim()}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: newFolderName.trim() ? '#5C4033' : '#ccc',
                                    color: newFolderName.trim() ? '#FFF5EE' : '#666',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: newFolderName.trim() ? 'pointer' : 'not-allowed',
                                    fontWeight: '500'
                                }}
                            >
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* NEW: Folder Deletion Confirmation Dialog */}
            {showDeleteFolderConfirm && selectedFolder && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fdf6e3',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '2px solid #DC2626',
                        minWidth: '300px',
                        maxWidth: '400px'
                    }}>
                        <h3 style={{
                            margin: '0 0 15px 0',
                            color: '#DC2626',
                            fontSize: '18px'
                        }}>
                            Delete Folder
                        </h3>
                        <p style={{
                            margin: '0 0 15px 0',
                            color: '#5C4033',
                            fontSize: '16px'
                        }}>
                            Are you sure you want to delete the folder "{selectedFolder.split('/').pop()}" and all its
                            contents? This action cannot be undone.
                        </p>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px'
                        }}>
                            <button
                                onClick={() => {
                                    setShowDeleteFolderConfirm(false);
                                    setSelectedFolder(null);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#ccc',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteFolder}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#DC2626',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* NEW: Add Canister Dialog */}
            {showAddCanisterDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fdf6e3',
                        padding: '20px',
                        borderRadius: '8px',
                        width: '400px',
                        boxShadow: '0px 4px 6px rgba(0,0,0,0.1)'
                    }}>
                        <h3 style={{
                            margin: '0 0 15px 0',
                            color: '#5C4033',
                            fontSize: '18px',
                            fontWeight: '500'
                        }}>
                            Add Canister to Project
                        </h3>

                        <div style={{marginBottom: '15px'}}>
                            <label style={{
                                display: 'block',
                                marginBottom: '5px',
                                color: '#5C4033',
                                fontSize: '14px'
                            }}>
                                Select Canister:
                            </label>

                            {isLoadingCanisters ? (
                                <div style={{
                                    padding: '8px 12px',
                                    color: '#666',
                                    fontSize: '14px'
                                }}>
                                    Loading available canisters...
                                </div>
                            ) : (
                                <select
                                    value={selectedCanisterToAdd}
                                    onChange={(e) => setSelectedCanisterToAdd(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        borderRadius: '4px',
                                        border: '1px solid #8B4513',
                                        backgroundColor: '#fff',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <option value="">Choose a canister...</option>
                                    {availableCanisters.map((canister) => (
                                        <option
                                            key={canister.principal.toText()}
                                            value={canister.principal.toText()}
                                        >
                                            {canister.canisterType === 'frontend' ? '🌐' : '⚙️'} {' '}
                                            {canister.name || canister.principal.toText()} {' '}
                                            ({canister.canisterType})
                                        </option>
                                    ))}
                                </select>
                            )}

                            {availableCanisters.length === 0 && !isLoadingCanisters && (
                                <div style={{
                                    padding: '8px 12px',
                                    color: '#8B4513',
                                    fontSize: '14px',
                                    fontStyle: 'italic',
                                    backgroundColor: '#fff5f5',
                                    border: '1px solid #fed7d7',
                                    borderRadius: '4px',
                                    marginTop: '5px'
                                }}>
                                    No available canisters to add. All your canisters are already in this project, or
                                    you haven't created any canisters yet.
                                </div>
                            )}
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px'
                        }}>
                            <button
                                onClick={() => {
                                    setShowAddCanisterDialog(false);
                                    setSelectedCanisterToAdd('');
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#8B4513',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddCanisterToProject}
                                disabled={!selectedCanisterToAdd}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: selectedCanisterToAdd ? '#5C4033' : '#ccc',
                                    color: selectedCanisterToAdd ? '#FFF5EE' : '#666',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: selectedCanisterToAdd ? 'pointer' : 'not-allowed',
                                    fontWeight: '500'
                                }}
                            >
                                Add to Project
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sidebar;